import type { Map as MapLibreMap, Point } from 'maplibre-gl';
import { delayColor } from '../format';
import type { Product, Vehicle } from '../types';

export type ColorMode = 'line' | 'delay';

/** Decides which vehicles are drawn and which are dimmed. */
export interface VehicleView {
  visible: (vehicle: Vehicle) => boolean;
  highlighted: (vehicle: Vehicle) => boolean;
}

type Detail = 'dot' | 'marker' | 'badge';

interface Animated {
  vehicle: Vehicle;
  fromLat: number;
  fromLon: number;
  toLat: number;
  toLon: number;
  startedAt: number;
  duration: number;
  bearing: number | null;
  /** 0 while appearing, 1 when fully drawn, back to 0 while disappearing. */
  presence: number;
  gone: boolean;
}

interface PickEntry {
  id: string;
  x: number;
  y: number;
  radius: number;
}

const APPEAR_MS = 450;
const DISAPPEAR_MS = 700;
/** A jump larger than this is a data glitch, not movement — snap instead. */
const SNAP_METERS = 4_000;
/** Grid used to keep line badges from stacking on top of each other. */
const LABEL_CELL_PX = 20;

/** Whichever vehicle is more interesting keeps its badge when two collide. */
const PRODUCT_RANK: Record<Product, number> = {
  express: 0,
  regional: 1,
  suburban: 2,
  subway: 3,
  tram: 4,
  ferry: 5,
  bus: 6,
  other: 7,
};

function lerp(a: number, b: number, t: number): number {
  return a + (b - a) * t;
}

function roughMeters(aLat: number, aLon: number, bLat: number, bLon: number): number {
  const dLat = (bLat - aLat) * 111_320;
  const dLon = (bLon - aLon) * 111_320 * Math.cos((aLat * Math.PI) / 180);
  return Math.hypot(dLat, dLon);
}

function roundRect(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  w: number,
  h: number,
  r: number,
): void {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.arcTo(x + w, y, x + w, y + h, r);
  ctx.arcTo(x + w, y + h, x, y + h, r);
  ctx.arcTo(x, y + h, x, y, r);
  ctx.arcTo(x, y, x + w, y, r);
  ctx.closePath();
}

/**
 * Draws all vehicles on a 2D canvas laid over the map.
 *
 * A canvas beats GeoJSON layers here: positions are re-interpolated every
 * animation frame, which would mean a full `setData` (and symbol re-placement)
 * at 60 Hz. Projecting a few thousand points per frame is far cheaper, and it
 * lets us draw real line badges instead of circles plus labels.
 */
export class VehicleLayer {
  private readonly canvas: HTMLCanvasElement;
  private readonly ctx: CanvasRenderingContext2D;
  private readonly items = new Map<string, Animated>();
  /** Stable, priority-sorted draw order so badge collisions resolve the same
   *  way frame after frame instead of flickering. */
  private order: Animated[] = [];
  private readonly textWidths = new Map<string, number>();
  private pickIndex: PickEntry[] = [];
  private frame = 0;
  private dpr = 1;
  private view: VehicleView = { visible: () => true, highlighted: () => true };
  private colorMode: ColorMode = 'line';
  private selectedId: string | null = null;
  private hoveredId: string | null = null;
  private running = false;

  constructor(private readonly map: MapLibreMap) {
    this.canvas = document.createElement('canvas');
    this.canvas.className = 'vehicle-canvas';
    Object.assign(this.canvas.style, {
      position: 'absolute',
      inset: '0',
      width: '100%',
      height: '100%',
      pointerEvents: 'none',
      zIndex: '3',
    });
    map.getCanvasContainer().appendChild(this.canvas);

    const ctx = this.canvas.getContext('2d');
    if (!ctx) throw new Error('2D canvas context unavailable');
    this.ctx = ctx;

    this.resize();
    map.on('resize', this.resize);
    document.addEventListener('visibilitychange', this.onVisibility);
    this.start();
  }

  destroy(): void {
    this.running = false;
    cancelAnimationFrame(this.frame);
    this.map.off('resize', this.resize);
    document.removeEventListener('visibilitychange', this.onVisibility);
    this.canvas.remove();
  }

  private onVisibility = (): void => {
    if (document.hidden) {
      this.running = false;
      cancelAnimationFrame(this.frame);
    } else {
      this.start();
    }
  };

  private start(): void {
    if (this.running) return;
    this.running = true;
    const loop = (): void => {
      if (!this.running) return;
      this.draw();
      this.frame = requestAnimationFrame(loop);
    };
    this.frame = requestAnimationFrame(loop);
  }

  private resize = (): void => {
    const container = this.map.getContainer();
    this.dpr = Math.min(window.devicePixelRatio || 1, 2);
    this.canvas.width = Math.round(container.clientWidth * this.dpr);
    this.canvas.height = Math.round(container.clientHeight * this.dpr);
  };

  setView(view: VehicleView): void {
    this.view = view;
  }

  setColorMode(mode: ColorMode): void {
    this.colorMode = mode;
  }

  setSelected(id: string | null): void {
    this.selectedId = id;
  }

  setHovered(id: string | null): void {
    this.hoveredId = id;
  }

  getVehicle(id: string): Vehicle | null {
    return this.items.get(id)?.vehicle ?? null;
  }

  /** Current interpolated position, so the camera can follow a vehicle. */
  getPosition(id: string): [number, number] | null {
    const item = this.items.get(id);
    if (!item) return null;
    const t = Math.min(1, (performance.now() - item.startedAt) / item.duration);
    return [lerp(item.fromLon, item.toLon, t), lerp(item.fromLat, item.toLat, t)];
  }

  vehicles(): Vehicle[] {
    const out: Vehicle[] = [];
    for (const item of this.items.values()) if (!item.gone) out.push(item.vehicle);
    return out;
  }

  /**
   * Feed a new snapshot. Every vehicle glides from where it is drawn right now
   * to its new position over `durationMs`, which keeps motion continuous even
   * though updates arrive in discrete polls.
   */
  update(vehicles: Vehicle[], durationMs: number): void {
    const now = performance.now();
    const seen = new Set<string>();

    for (const vehicle of vehicles) {
      seen.add(vehicle.id);
      const existing = this.items.get(vehicle.id);
      if (!existing) {
        this.items.set(vehicle.id, {
          vehicle,
          fromLat: vehicle.lat,
          fromLon: vehicle.lon,
          toLat: vehicle.lat,
          toLon: vehicle.lon,
          startedAt: now,
          duration: durationMs,
          bearing: vehicle.bearing,
          presence: 0,
          gone: false,
        });
        continue;
      }

      const t = Math.min(1, (now - existing.startedAt) / existing.duration);
      const currentLat = lerp(existing.fromLat, existing.toLat, t);
      const currentLon = lerp(existing.fromLon, existing.toLon, t);
      const jumped = roughMeters(currentLat, currentLon, vehicle.lat, vehicle.lon) > SNAP_METERS;

      existing.vehicle = vehicle;
      existing.fromLat = jumped ? vehicle.lat : currentLat;
      existing.fromLon = jumped ? vehicle.lon : currentLon;
      existing.toLat = vehicle.lat;
      existing.toLon = vehicle.lon;
      existing.startedAt = now;
      existing.duration = durationMs;
      existing.bearing = vehicle.bearing ?? existing.bearing;
      existing.gone = false;
    }

    for (const [id, item] of this.items) {
      if (!seen.has(id) && !item.gone) {
        item.gone = true;
        item.startedAt = now;
        item.duration = DISAPPEAR_MS;
      }
    }

    this.order = [...this.items.values()].sort((a, b) => {
      const rank = PRODUCT_RANK[a.vehicle.line.product] - PRODUCT_RANK[b.vehicle.line.product];
      return rank !== 0 ? rank : a.vehicle.id.localeCompare(b.vehicle.id);
    });
  }

  /** Nearest drawn vehicle to a point in CSS pixels, if any is close enough. */
  pick(x: number, y: number): Vehicle | null {
    let best: PickEntry | null = null;
    let bestDistance = Infinity;
    for (const entry of this.pickIndex) {
      const distance = Math.hypot(entry.x - x, entry.y - y);
      if (distance <= entry.radius + 6 && distance < bestDistance) {
        best = entry;
        bestDistance = distance;
      }
    }
    return best ? (this.items.get(best.id)?.vehicle ?? null) : null;
  }

  private textWidth(label: string, font: string): number {
    const key = `${font}|${label}`;
    let width = this.textWidths.get(key);
    if (width === undefined) {
      this.ctx.font = font;
      width = this.ctx.measureText(label).width;
      this.textWidths.set(key, width);
    }
    return width;
  }

  private colorOf(vehicle: Vehicle): string {
    return this.colorMode === 'delay' ? delayColor(vehicle.delay) : vehicle.line.bg;
  }

  /** Reserve screen space for a badge; false when something is already there. */
  private claim(occupied: Set<number>, x: number, y: number, halfW: number, halfH: number): boolean {
    const x0 = Math.floor((x - halfW) / LABEL_CELL_PX);
    const x1 = Math.floor((x + halfW) / LABEL_CELL_PX);
    const y0 = Math.floor((y - halfH) / LABEL_CELL_PX);
    const y1 = Math.floor((y + halfH) / LABEL_CELL_PX);
    for (let cx = x0; cx <= x1; cx += 1) {
      for (let cy = y0; cy <= y1; cy += 1) {
        if (occupied.has(cx * 100_000 + cy)) return false;
      }
    }
    for (let cx = x0; cx <= x1; cx += 1) {
      for (let cy = y0; cy <= y1; cy += 1) occupied.add(cx * 100_000 + cy);
    }
    return true;
  }

  private draw(): void {
    const { ctx } = this;
    ctx.clearRect(0, 0, this.canvas.width, this.canvas.height);

    const now = performance.now();

    // Lifecycle first: grow newcomers in, fade leavers out, drop the finished.
    for (const [id, item] of this.items) {
      if (item.gone) {
        item.presence = 1 - Math.min(1, (now - item.startedAt) / DISAPPEAR_MS);
        if (item.presence <= 0) this.items.delete(id);
      } else if (item.presence < 1) {
        item.presence = Math.min(1, item.presence + 16 / APPEAR_MS);
      }
    }

    const zoom = this.map.getZoom();
    const detail: Detail = zoom < 11 ? 'dot' : zoom < 13 ? 'marker' : 'badge';
    const marginCss = 60;
    const widthCss = this.canvas.width / this.dpr;
    const heightCss = this.canvas.height / this.dpr;

    this.pickIndex = [];
    const occupied = new Set<number>();
    const highlighted: { item: Animated; point: Point }[] = [];
    const dimmed: { item: Animated; point: Point }[] = [];
    let selected: { item: Animated; point: Point } | null = null;

    for (const item of this.order) {
      if (!this.items.has(item.vehicle.id)) continue;
      if (!this.view.visible(item.vehicle)) continue;

      const t = Math.min(1, (now - item.startedAt) / item.duration);
      const lat = item.gone ? item.toLat : lerp(item.fromLat, item.toLat, t);
      const lon = item.gone ? item.toLon : lerp(item.fromLon, item.toLon, t);
      const point = this.map.project([lon, lat]);
      if (
        point.x < -marginCss ||
        point.y < -marginCss ||
        point.x > widthCss + marginCss ||
        point.y > heightCss + marginCss
      ) {
        continue;
      }

      if (item.vehicle.id === this.selectedId) selected = { item, point };
      else if (this.view.highlighted(item.vehicle)) highlighted.push({ item, point });
      else dimmed.push({ item, point });
    }

    // Search hits claim badge space first, then get drawn on top of the rest.
    const details = new Map<Animated, Detail>();
    for (const group of [highlighted, dimmed]) {
      for (const { item, point } of group) {
        let effective: Detail = detail;
        if (detail === 'badge') {
          const { halfWidth, halfHeight } = this.badgeSize(item.vehicle.line.name);
          // Too crowded for a readable badge? Fall back to a plain marker.
          if (!this.claim(occupied, point.x, point.y, halfWidth, halfHeight)) effective = 'marker';
        }
        details.set(item, effective);
      }
    }

    for (const { item, point } of dimmed) {
      this.drawOne(item, point, details.get(item) ?? detail, false);
    }
    for (const { item, point } of highlighted) {
      this.drawOne(item, point, details.get(item) ?? detail, false);
    }

    // The selected vehicle is drawn last so nothing can overlap it.
    if (selected) this.drawOne(selected.item, selected.point, detail, true);
  }

  private badgeSize(label: string): { halfWidth: number; halfHeight: number } {
    const font = '700 11.5px Inter, system-ui, sans-serif';
    return { halfWidth: Math.max(this.textWidth(label, font) + 12, 20) / 2, halfHeight: 9 };
  }

  private drawOne(item: Animated, point: Point, detail: Detail, isSelected: boolean): void {
    const { ctx, dpr } = this;
    const x = point.x * dpr;
    const y = point.y * dpr;

    const vehicle = item.vehicle;
    const highlighted = this.view.highlighted(vehicle);
    const isHovered = vehicle.id === this.hoveredId;
    // Geschaetzte Positionen werden blasser gezeichnet, siehe Vehicle.positionKind.
    const estimated = vehicle.positionKind === 'estimated';
    const alpha = item.presence * (highlighted ? 1 : 0.22) * (estimated ? 0.72 : 1);
    if (alpha <= 0.01) return;

    const color = this.colorOf(vehicle);
    const scale = (isSelected ? 1.18 : isHovered ? 1.1 : 1) * (0.55 + 0.45 * item.presence);

    ctx.save();
    ctx.globalAlpha = alpha;

    if (isSelected || isHovered) {
      ctx.beginPath();
      ctx.arc(x, y, (detail === 'badge' ? 22 : 15) * dpr * scale, 0, Math.PI * 2);
      ctx.fillStyle = color;
      ctx.globalAlpha = alpha * 0.18;
      ctx.fill();
      ctx.globalAlpha = alpha;
    }

    if (detail === 'dot') {
      ctx.beginPath();
      ctx.arc(x, y, 3.1 * dpr * scale, 0, Math.PI * 2);
      ctx.fillStyle = color;
      ctx.fill();
      this.pickIndex.push({ id: vehicle.id, x: point.x, y: point.y, radius: 7 });
      ctx.restore();
      return;
    }

    if (item.bearing !== null) this.drawHeading(x, y, item.bearing, color, detail, scale);

    if (detail === 'marker') {
      ctx.beginPath();
      ctx.arc(x, y, 5.4 * dpr * scale, 0, Math.PI * 2);
      ctx.fillStyle = color;
      ctx.fill();
      ctx.lineWidth = 1.6 * dpr;
      ctx.strokeStyle = isSelected ? '#ffffff' : 'rgba(0,0,0,0.35)';
      // Gestrichelt heisst berechnet. Das Zuruecksetzen ist Pflicht: der Kontext
      // wird fuer alle Fahrzeuge wiederverwendet.
      if (estimated) ctx.setLineDash([3 * dpr, 2 * dpr]);
      ctx.stroke();
      ctx.setLineDash([]);
      this.pickIndex.push({ id: vehicle.id, x: point.x, y: point.y, radius: 9 });
      ctx.restore();
      return;
    }

    // Badge: the line name inside a coloured pill, like a real line number.
    const label = vehicle.line.name;
    const fontSize = 11.5 * dpr * scale;
    const font = `700 ${fontSize.toFixed(1)}px Inter, system-ui, sans-serif`;
    const padding = 6 * dpr * scale;
    const w = Math.max(this.textWidth(label, font) + padding * 2, 20 * dpr * scale);
    const h = 17 * dpr * scale;

    ctx.shadowColor = 'rgba(0,0,0,0.35)';
    ctx.shadowBlur = 6 * dpr;
    ctx.shadowOffsetY = 1.5 * dpr;
    roundRect(ctx, x - w / 2, y - h / 2, w, h, h / 2.6);
    ctx.fillStyle = color;
    ctx.fill();
    ctx.shadowColor = 'transparent';
    ctx.shadowBlur = 0;
    ctx.shadowOffsetY = 0;

    if (isSelected) {
      ctx.lineWidth = 2 * dpr;
      ctx.strokeStyle = '#ffffff';
      ctx.stroke();
    }

    ctx.font = font;
    ctx.fillStyle = this.colorMode === 'delay' ? '#0b0e14' : vehicle.line.fg;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText(label, x, y + 0.5 * dpr);

    // In line-colour mode a small dot still surfaces the delay at a glance.
    if (this.colorMode === 'line' && vehicle.delay !== null && vehicle.delay >= 180) {
      ctx.beginPath();
      ctx.arc(x + w / 2 - dpr, y - h / 2 + dpr, 3.2 * dpr * scale, 0, Math.PI * 2);
      ctx.fillStyle = delayColor(vehicle.delay);
      ctx.fill();
      ctx.lineWidth = 1.2 * dpr;
      ctx.strokeStyle = 'rgba(0,0,0,0.35)';
      ctx.stroke();
    }

    this.pickIndex.push({
      id: vehicle.id,
      x: point.x,
      y: point.y,
      radius: Math.max(w, h) / (2 * dpr),
    });
    ctx.restore();
  }

  private drawHeading(
    x: number,
    y: number,
    bearing: number,
    color: string,
    detail: 'marker' | 'badge',
    scale: number,
  ): void {
    const { ctx, dpr } = this;
    const bearingRad = ((bearing - 90) * Math.PI) / 180;
    const distance = (detail === 'badge' ? 15 : 11) * dpr * scale;
    const size = (detail === 'badge' ? 5 : 4) * dpr * scale;

    ctx.save();
    ctx.translate(x + Math.cos(bearingRad) * distance, y + Math.sin(bearingRad) * distance);
    ctx.rotate(bearingRad);
    ctx.beginPath();
    ctx.moveTo(size, 0);
    ctx.lineTo(-size * 0.8, size * 0.85);
    ctx.lineTo(-size * 0.8, -size * 0.85);
    ctx.closePath();
    ctx.fillStyle = color;
    ctx.fill();
    ctx.restore();
  }
}
